import { useCallback, useMemo, useState } from 'react';
import {
  CsvColumnMapping,
  CsvImporterConfig,
  CsvImportStatus,
  CsvParsedRow,
  CsvRawRow,
  CsvValidationContext,
  CsvManualOverrideMap,
  CsvImportMetaState,
  CsvEncoding,
} from './csv-import.types';
import { parseCsvFile, reparseCsvFileWithEncoding, validateCsvRows } from './csv-import.utils';

export type CsvImporterMode = 'idle' | 'mapping' | 'review' | 'importing' | 'done';

export interface UseCsvImportStateResult<TDto = any> {
  file: File | null;
  headers: string[];
  rows: CsvRawRow[];
  mapping: CsvColumnMapping<TDto>;
  parsedRows: CsvParsedRow<TDto>[];
  status: CsvImportStatus;
  mode: CsvImporterMode;
  manualOverrides: CsvManualOverrideMap;
  meta: CsvImportMetaState | null;
  setFile: (file: File | null) => void;
  setMapping: (updater: (prev: CsvColumnMapping<TDto>) => CsvColumnMapping<TDto>) => void;
  setMode: (mode: CsvImporterMode) => void;
  setParsedRows: (rows: CsvParsedRow<TDto>[]) => void;
  setStatus: (updater: (prev: CsvImportStatus) => CsvImportStatus) => void;
  setManualOverrides: (updater: (prev: CsvManualOverrideMap) => CsvManualOverrideMap) => void;
  setEncoding: (encoding: CsvEncoding) => void;
  reset: () => void; // NEU: kompletten Import-State zurücksetzen
}

export function useCsvImportState<TDto = any>(): UseCsvImportStateResult<TDto> {
  const [file, setFileInternal] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<CsvRawRow[]>([]);
  const [mapping, setMappingState] = useState<CsvColumnMapping<TDto>>({});
  const [parsedRows, setParsedRows] = useState<CsvParsedRow<TDto>[]>([]);
  const [status, setStatusInternal] = useState<CsvImportStatus>({
    totalRows: 0,
    validRows: 0,
    errorRows: 0,
    importedRows: 0,
    failedRows: 0,
  });
  const [mode, setMode] = useState<CsvImporterMode>('idle');
  const [manualOverrides, setManualOverrides] = useState<CsvManualOverrideMap>({});
  const [meta, setMeta] = useState<CsvImportMetaState | null>(null);

  const setStatus = useCallback(
    (updater: (prev: CsvImportStatus) => CsvImportStatus) => {
      setStatusInternal((prev) => updater(prev));
    },
    [],
  );

  const setFile = useCallback((newFile: File | null) => {
    setFileInternal(newFile);
    if (!newFile) {
      setHeaders([]);
      setRows([]);
      setMappingState({});
      setParsedRows([]);
      setStatusInternal({
        totalRows: 0,
        validRows: 0,
        errorRows: 0,
        importedRows: 0,
        failedRows: 0,
      });
      setMode('idle');
      setManualOverrides({});
      setMeta(null);
      return;
    }

    parseCsvFile(newFile)
      .then(({ headers, rows, meta }) => {
        setHeaders(headers);
        setRows(rows);
        setMode('mapping');
        setStatusInternal((prev) => ({ ...prev, totalRows: rows.length }));
        setMeta(meta);
      })
      .catch(() => {
        setHeaders([]);
        setRows([]);
        setMode('idle');
        setMeta(null);
      });
  }, []);

  const setMapping = useCallback((updater: (prev: CsvColumnMapping<TDto>) => CsvColumnMapping<TDto>) => {
    setMappingState((prev) => updater(prev));
  }, []);

  const reset = useCallback(() => {
    setFileInternal(null);
    setHeaders([]);
    setRows([]);
    setMappingState({});
    setParsedRows([]);
    setStatusInternal({
      totalRows: 0,
      validRows: 0,
      errorRows: 0,
      importedRows: 0,
      failedRows: 0,
    });
    setMode('idle');
    setManualOverrides({});
    setMeta(null);
  }, []);

  const setEncoding = useCallback(
    async (encoding: CsvEncoding) => {
      // Wenn keine Datei vorhanden ist, können wir nichts neu parsen
      if (!file) {
        setMeta((prev) => {
          if (!prev || !prev.encoding) return prev;
          return {
            ...prev,
            encoding: {
              ...prev.encoding,
              selected: encoding,
            },
          };
        });
        return;
      }

      // Datei mit dem gewählten Encoding neu einlesen und State aktualisieren
      try {
        const { headers, rows, meta } = await reparseCsvFileWithEncoding(file, encoding);
        setHeaders(headers);
        setRows(rows);
        setMeta(meta);
        setStatusInternal((prev) => ({
          ...prev,
          totalRows: rows.length,
          validRows: 0,
          errorRows: 0,
        }));
        setParsedRows([]);
        setMode('mapping');
      } catch {
        // Falls etwas schiefgeht, nur selected-Encoding in Meta aktualisieren
        setMeta((prev) => {
          if (!prev || !prev.encoding) return prev;
          return {
            ...prev,
            encoding: {
              ...prev.encoding,
              selected: encoding,
            },
          };
        });
      }
    },
    [file],
  );

  return {
    file,
    headers,
    rows,
    mapping,
    parsedRows,
    status,
    mode,
    manualOverrides,
    meta,
    setFile,
    setMapping,
    setMode,
    setParsedRows,
    setStatus,
    setManualOverrides,
    setEncoding,
    reset,
  };
}

export interface UseCsvValidationResult<TDto = any, TMeta = any> {
  context: CsvValidationContext<TMeta> | null;
  isValidating: boolean;
  hasErrors: boolean;
  previewRows: CsvParsedRow<TDto>[];
  revalidate: () => void;
}

export function useCsvValidation<TDto = any, TMeta = any>(
  config: CsvImporterConfig<TDto, TMeta>,
  state: UseCsvImportStateResult<TDto>,
): UseCsvValidationResult<TDto, TMeta> {
  const [context, setContext] = useState<CsvValidationContext<TMeta> | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  const { rows, mapping, setParsedRows, setStatus, setMode, manualOverrides } = state;

  const doValidate = useCallback(async () => {
    if (!rows.length) {
      setParsedRows([]);
      setStatus((prev) => ({ ...prev, validRows: 0, errorRows: 0 }));
      return;
    }

    setIsValidating(true);
    try {
      let ctx: CsvValidationContext<TMeta> = {};
      if (config.buildValidationContext) {
        const result = await config.buildValidationContext();
        ctx = result as CsvValidationContext<TMeta>;
      }
      setContext(ctx);

      const parsed = await validateCsvRows<TDto, TMeta>(
        rows,
        mapping,
        config,
        ctx,
        manualOverrides,
      );

      const validRows = parsed.filter((r) => r.errors.length === 0).length;
      const errorRows = parsed.length - validRows;

      setParsedRows(parsed);
      setStatus((prev) => ({
        ...prev,
        totalRows: parsed.length,
        validRows,
        errorRows,
      }));
      setMode('review');
    } finally {
      setIsValidating(false);
    }
  }, [rows, mapping, setParsedRows, setStatus, setMode, config, manualOverrides]);

  const hasErrors = useMemo(
    () => state.parsedRows.some((r) => r.errors.length > 0),
    [state.parsedRows],
  );

  const previewRows = useMemo(() => {
    return state.parsedRows;
  }, [state.parsedRows]);

  return {
    context,
    isValidating,
    hasErrors,
    previewRows,
    revalidate: doValidate,
  };
}

export interface UseCsvImportExecutorResult {
  isImporting: boolean;
  startImport: (rowsOverride?: CsvParsedRow<any>[]) => Promise<void>;
}

export function useCsvImportExecutor<TDto = any, TMeta = any>(
  config: CsvImporterConfig<TDto, TMeta>,
  state: UseCsvImportStateResult<TDto>,
): UseCsvImportExecutorResult {
  const [isImporting, setIsImporting] = useState(false);

  const startImport = useCallback(
    async (rowsOverride?: CsvParsedRow<TDto>[]) => {
      const sourceRows = rowsOverride ?? state.parsedRows;
      const validRows = sourceRows.filter((r) => r.errors.length === 0);
      if (!validRows.length) return;

      setIsImporting(true);
      state.setMode('importing');

      // Falls ein Batch-Endpoint konfiguriert ist, diesen bevorzugt nutzen
      if (config.importEndpoint && config.mode) {
        try {
          const dtoRows = validRows.map((r) => r.mappedValues as TDto);
          const result = await config.importEndpoint({ mode: config.mode, rows: dtoRows });

          // Import-/Fehlerzähler aus Ergebnis ableiten
          let imported = 0;
          let failed = 0;
          result.results.forEach((rowResult) => {
            if (rowResult.success) imported += 1;
            else failed += 1;
          });

          state.setStatus((prev: CsvImportStatus) => ({
            ...prev,
            importedRows: imported,
            failedRows: failed,
          }));
        } finally {
          state.setMode('done');
          setIsImporting(false);
        }
        return;
      }

      // Fallback: bisheriges Verhalten (pro Zeile onSubmitRow aufrufen)
      let imported = 0;
      let failed = 0;

      for (const row of validRows) {
        try {
          await config.onSubmitRow(row.mappedValues as TDto);
          imported += 1;
        } catch (e) {
          failed += 1;
        }
        const importedCount = imported;
        const failedCount = failed;
        state.setStatus((prev) => ({
          ...prev,
          importedRows: importedCount,
          failedRows: failedCount,
        }));
      }

      state.setMode('done');
      setIsImporting(false);
    },
    [config, state],
  );

  return {
    isImporting,
    startImport,
  };
}
